import { Component, OnInit, ViewEncapsulation } from '@angular/core';
import { NavigationEnd, Router } from '@angular/router';
import { getCurrentLanguage as GetCurrentLanguage, NotificationService, ShellDomainProvider, State } from '@ecap3/core';
import { TemplateAtlasSharedService } from '@ecap3/template-atlas';
import { Store } from '@ngrx/store';
import { TranslateService } from '@ngx-translate/core';
import { clone, forEach } from 'lodash';
import { BehaviorSubject, Observable, Subject, Subscription } from 'rxjs';
import { takeUntil } from 'rxjs/operators';

@Component({
    selector: 'fuse-quick-panel',
    templateUrl: './quick-panel.component.html',
    styleUrls: ['./quick-panel.component.scss'],
    encapsulation: ViewEncapsulation.None
})
export class FuseQuickPanelComponent implements OnInit {
    resultUnreadMessage: any;
    resultNotifications: any;
    lang$: Observable<string>;
    langSub: Subscription;
    currentLang: string;
    botImage =
        ShellDomainProvider['api'] &&
        ShellDomainProvider['api']['CdnTenantBaseUrl'] +
            'assets/images/bot.svg';
    date: Date;
    settings: any;
    notes: any[];
    events: any[];
    newMessageArrivedTitle: string;
    defaultSenderTitle: string;
    notifications: any[] = [];
    messageMaxLength = 50;
    currentPage: string;
    currentLangObservable: BehaviorSubject<string>;
    totalNotification = 0;
    displayingNotificationcount = 0;
    private _unsubscribeAll: Subject<any>;
    constructor(
        private translateService: TranslateService,
        private sharedService: TemplateAtlasSharedService,
        private router: Router,
        private notificationService: NotificationService,
        private store: Store<State>
    ) {
        this.lang$ = this.store.select(GetCurrentLanguage);

        this._unsubscribeAll = new Subject();
        this.date = new Date();
        this.settings = {
            notify: true,
            cloud: false,
            retro: true
        };
        // when all unread Message arrived
        this.sharedService.unreadMessageArrived
            .pipe(takeUntil(this._unsubscribeAll))
            .subscribe((result: any) => {
                this.notifications = [];
                if (result && result.notifications.length) {
                    this.totalNotification = result.total;
                    this.resultUnreadMessage = result.notifications;
                    this.displayingNotificationcount =
                        result.notifications.length;

                    this.denormalizeNotifications(
                        result.notifications,
                        this.notifications
                    );
                }
            });
        // when view all clicked
        this.sharedService.viewAllArrived
            .pipe(takeUntil(this._unsubscribeAll))
            .subscribe((result: any) => {
                if (result && result.notifications.length) {
                    this.totalNotification = result.total;
                    this.resultNotifications = result.notifications;
                    this.displayingNotificationcount =
                        this.displayingNotificationcount +
                        result.notifications.length;
                    this.denormalizeNotifications(
                        result.notifications,
                        this.notifications
                    );
                }
            });
        // getting current page route
        this.router.events
            .pipe(takeUntil(this._unsubscribeAll))
            .subscribe((event: any) => {
                if (event instanceof NavigationEnd) {
                    console.log('this.router.url', this.router.url);

                    this.currentPage = this.router.url;
                }
            });
        this.lang$.pipe(takeUntil(this._unsubscribeAll)).subscribe((lang) => {
            this.currentLang = lang;
            this.notifications = [];

            this.denormalizeNotifications(
                this.resultNotifications,
                this.notifications
            );
            this.denormalizeNotifications(
                this.resultUnreadMessage,
                this.notifications
            );
        });
    }

    ngOnInit() {
        // translation related code
        // this.translateService.instant('NEW_MESSAGE_NOTIFICATION_TITLE')
        //     .pipe(takeUntil(this._unsubscribeAll))
        //     .subscribe(val => this.newMessageArrivedTitle = val);
        // this.translateService.instant('DEFAULT_SENDER_TITLE')
        //     .pipe(takeUntil(this._unsubscribeAll))
        //     .subscribe(val => this.defaultSenderTitle = val);
        // translation ends
    }

    denormalizeNotifications(notifications, notificationStore): void {
        const maxlength = this.messageMaxLength;
        const defaultNewMessage = this.newMessageArrivedTitle;
        const defaultSenderTitle = this.defaultSenderTitle;
        const schedulingKeys = {
            'Select-Network-Expiring-Contract': 'Expiring Contract',
            'Select-Network-Missing-Insure': 'Missing Policy',
            'Select-Network-Missing-Asset-Insure': 'Missing Policy'
        };
        const isJSON = this.isJSON;

        notifications.forEach((notification) => {
            this.processDenormalizedPayload(notification, schedulingKeys, isJSON);
            this.processTitle(notification, defaultNewMessage);
            this.processSenderName(notification, defaultSenderTitle);
            this.processMessage(notification, maxlength, defaultNewMessage);

            this.updateDetailsInfo(notification);
            notificationStore.push(notification);
        });

    }

    private processDenormalizedPayload(notification, schedulingKeys, isJSON): void {
        const payload = notification.Payload;
        notification.DenormalizedPayload = notification.DenormalizedPayload
            ? (isJSON(notification.DenormalizedPayload)
                ? JSON.parse(notification.DenormalizedPayload)
                : notification.DenormalizedPayload)
            : {};

        if (payload.ResponseKey === 'SchedulingNotification') {
            notification.DenormalizedPayload['title'] = schedulingKeys[payload.SubscriptionFilters[0].Context];
        }
    }

    private processTitle(notification, defaultNewMessage): void {
        const denormalizedPayload = notification.DenormalizedPayload;

        if (denormalizedPayload.titleKey) {
            this.translateService
                .instant(denormalizedPayload.titleKey)
                .pipe(takeUntil(this._unsubscribeAll))
                .subscribe((val) => {
                    denormalizedPayload['title'] = this.replacePlaceholderValues(val, denormalizedPayload) || defaultNewMessage;
                });
        } else if (denormalizedPayload.Title) {
            this.translateService
                .instant(denormalizedPayload.Title)
                .pipe(takeUntil(this._unsubscribeAll))
                .subscribe((val) => {
                    denormalizedPayload['title'] = this.replacePlaceholderValues(val, denormalizedPayload) || defaultNewMessage;
                });
        }
    }

    private processSenderName(notification, defaultSenderTitle): void {
        notification.DenormalizedPayload['senderName'] = notification.DenormalizedPayload.senderName || defaultSenderTitle;
    }

    private processMessage(notification, maxlength: number, defaultNewMessage: string): void {
        const denormalizedPayload = notification.DenormalizedPayload;

        if (denormalizedPayload.messageKey) {
            this.translateService
                .instant(denormalizedPayload.messageKey)
                .pipe(takeUntil(this._unsubscribeAll))
                .subscribe((val) => {
                    denormalizedPayload['message'] = this.replacePlaceholderValues(val, denormalizedPayload);
                });
        } else if (denormalizedPayload.key) {
            this.translateService
                .instant(denormalizedPayload.key)
                .pipe(takeUntil(this._unsubscribeAll))
                .subscribe((val) => {
                    denormalizedPayload['message'] = this.replacePlaceholderValues(val, denormalizedPayload.messageObj);
                });
        } else if (denormalizedPayload.Message && denormalizedPayload.Message.indexOf(' ') <= -1) {
            this.translateService
                .instant(denormalizedPayload.Message)
                .pipe(takeUntil(this._unsubscribeAll))
                .subscribe((val) => {
                    denormalizedPayload['message'] = this.replacePlaceholderValues(val, denormalizedPayload);
                });
        }

        this.truncateMessageIfNeeded(notification, maxlength, defaultNewMessage);
    }

    private truncateMessageIfNeeded(notification, maxlength: number, defaultNewMessage: string): void {
        let message;
        if (
            notification.DenormalizedPayload &&
            notification.DenormalizedPayload.message
        ) {
            message = notification.DenormalizedPayload.message;
        }
        if (message && message.length > maxlength) {
            notification.DenormalizedPayload['message'] = `${message.slice(
                0,
                maxlength
            )}...`;
        } else if (!message) {
            notification.DenormalizedPayload['message'] = defaultNewMessage;
        }
    }

    private updateDetailsInfo(notification): void {
        const payload = notification.Payload;
        const denormalizedPayload = notification.DenormalizedPayload;

        notification.DetailsInfo = {
            Identifier: clone(payload.ResponseKey),
            Email: clone(denormalizedPayload.Email) || null,
            NotificationID: clone(notification.Id) || null,
            ThreadId: this.getThreadId(payload, denormalizedPayload)
        };

        delete denormalizedPayload.ThreadId;
        delete denormalizedPayload.Email;
    }

    private getThreadId(payload, denormalizedPayload): string | null {
        if (payload.ResponseValue && JSON.parse(payload.ResponseValue).ThreadId) {
            return JSON.parse(payload.ResponseValue).ThreadId;
        } else if (denormalizedPayload.ThreadId) {
            return denormalizedPayload.ThreadId;
        }
        return null;
    }


    replacePlaceholderValues(text, valueObj) {
        forEach(valueObj, function (value, key) {
            // text = text.replace('[' + key + ']', value);
            if (value) {
                text = text.split('[' + key + ']').join(value);
            } else {
                text = text.split('[' + key + ']').join('');
            }
        });
        return text;
    }
    /**
     * Title: reloadNotificationPanel
     * Description: reload notification panel after click event
     * @param Observable result
     * @return any
     */
    reloadNotificationPanel(result) {
        if (
            result.body &&
            result.body.ErrorMessages &&
            !result.body.ErrorMessages.length
        ) {
            this.sharedService.notifyNewUnreadMessage(0, 10, true);
        }
    }

    /**
     * Title: updateNotificationCounter
     * Description: updating notification counter
     * params none
     * return void
     */
    updateNotificationCounter() {
        this.notificationService
            .getUnreadNotificationCounter()
            .pipe(takeUntil(this._unsubscribeAll))
            .subscribe((counter: number) => {
                this.sharedService.notificationMarkAsRead(counter);
            });
    }

    isJSON(str) {
        try {
            let obj = JSON.parse(str);
            if (obj && typeof obj === 'object' && obj !== null) {
                return true;
            }
        } catch (err) {}
        return false;
    }

    ngOnDestroy() {
        this._unsubscribeAll.next(null as any);
        this._unsubscribeAll.complete();
    }
}
